import React, { useEffect } from "react";
import { NextPage } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { FetchMoreOptions } from "@apollo/client";
import { useQuery } from "@apollo/client";

import { useUser } from "src/hooks/useUser";
import { isUser } from "src/lib/globalTypes";
import { GetUserOrOrgQuery, UserProjectsArgs } from "src/lib/graphql/types";
import { getUserOrOrgQuery } from "src/lib/graphql/user.queries";

import { socialMetadata } from "src/components/layout/Head";
import ProfileHeader from "src/components/index/Profile/ProfileHeader";
import ProfileMain from "src/components/index/Profile/ProfileMain";
import ProfileSidebar from "src/components/index/ProfileSidebar";
import NotFound from "src/pages/404";
import ProfileHeaderLoader from "src/components/index/Profile/ProfileHeaderLoader";
import ProfileMainLoader from "src/components/index/Profile/ProfileMainLoader";

export type FetchMoreProfileProjects = (
  options: FetchMoreOptions & { variables: UserProjectsArgs }
) => void;

export type RefetchProfileProjects = (variables: UserProjectsArgs) => void;

const IndexProfile: NextPage = () => {
  const [me] = useUser();

  const router = useRouter();
  const { shortname } = router.query;

  const { refetch, loading, data } = useQuery<GetUserOrOrgQuery>(
    getUserOrOrgQuery,
    {
      variables: {
        shortname,
        withProjects: true,
      },
      errorPolicy: "all",
    }
  );

  useEffect(() => {
    if (me) {
      refetch();
    }
  }, [me?.id]);

  /** @todo fix type casting to any below */
  const entity = data?.userOrOrg;

  return (
    <>
      <Head>
        {socialMetadata({
          title:
            entity &&
            `${
              "fullName" in entity ? entity.fullName : entity.name
            }'s Profile - HASH`,
          url: `https://hash.ai/@${shortname}`,
          image: entity && ("avatar" in entity ? entity.avatar : entity.image),
          description: isUser(entity)
            ? entity.biography
            : (entity as any)?.description,
        })}
        <link rel="canonical" href={`https://hash.ai/@${shortname}`} />
      </Head>
      {loading ? (
        <div className="index-profile-page">
          <ProfileHeaderLoader />
          <ProfileMainLoader />
        </div>
      ) : entity ? (
        <div className="index-profile-page">
          <ProfileHeader entity={entity as any} />
          <ProfileMain entity={entity as any} />
          <ProfileSidebar entity={entity as any} />
        </div>
      ) : (
        <NotFound />
      )}
    </>
  );
};

export default IndexProfile;
